'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CommandGroup, CommandItem, CommandList } from '@/components/ui/app-command';
import { Icon } from '@/components/ui/icon';
import { Clock01Icon, Loading03Icon, SearchRemoveIcon } from '@/components/ui/icon/glyphs';
import { LiveEventRowSearchItem, type LiveEventSearchResult } from './live-event-row-search-item';
import { OutcomeRow } from './outcome-row';
import { BlendedRowsList, buildMarketRow, buildProposalRow, buildUserRow, CreatorRowsList, MarketRowsList, OutcomeRowsList, ProposalRowsList, type RowSharedProps, type TrendingMarket, trendingRow, UserRowsList, WinnerRowsList, winnerHref } from './search-modal-rows';
import { hasTabResults } from './search-modal-state';
import type { BlendedSearchResult, CreatorSearchResult, MarketSearchResult, OutcomeSearchResult, ProposalSearchResult, SearchTab, UserSearchResult, WinnerSearchResult } from './search-types';
import { WinnerRow } from './winner-row';
export interface SearchResultsListProps {
  shouldSearch: boolean;
  isLoading: boolean;
  isPending: boolean;
  activeTab: SearchTab;
  activeTabHasResults: boolean;
  hasResults: boolean;
  hasExactMatch: boolean;
  exactMatchPubkey: string | null;
  showFuzzyGroups: boolean;
  mode: string;
  tickerHighlighted: boolean;
  blended: ReadonlyArray<BlendedSearchResult>;
  markets: ReadonlyArray<MarketSearchResult>;
  outcomes: ReadonlyArray<OutcomeSearchResult>;
  winners: ReadonlyArray<WinnerSearchResult>;
  proposals: ReadonlyArray<ProposalSearchResult>;
  creators: ReadonlyArray<CreatorSearchResult>;
  users: ReadonlyArray<UserSearchResult>;
  liveEvents: ReadonlyArray<LiveEventSearchResult>;
  marketByPubkey: Map<string, MarketSearchResult>;
  proposalById: Map<string, ProposalSearchResult>;
  recentEntries: ReadonlyArray<{
    query: string;
    ts: number;
  }>;
  trendingMarkets: ReadonlyArray<TrendingMarket>;
  showTrending: boolean;
  rowShared: RowSharedProps;
  onRecentSelect: (query: string) => void;
  onClearRecent: () => void;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverWinner: (mint: string) => void;
}
export function SearchResultsList(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <CommandList className="overflow-y-auto p-2 sm:max-h-80"><SearchIdleState {...props} /><SearchExactMatchGroup {...props} /><SearchAllGroups {...props} /><SearchTabGroups {...props} /></CommandList>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function SearchIdleState(props) {
  const $ = _c(9);
  let t0;
  if ($[0] !== props.isLoading) {
    t0 = props.isLoading ? <div className="flex items-center justify-center py-12"><Icon icon={Loading03Icon} className="size-5 animate-spin text-foreground-secondary" /></div> : null;
    $[0] = props.isLoading;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  if ($[2] !== props) {
    t1 = <SearchRecentState {...props} />;
    t2 = <SearchNoResultsState {...props} />;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = <>{t0}{t1}{t2}</>;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SearchRecentState(props) {
  const $ = _c(22);
  const t = useTranslations("search");
  const tMarkets = useTranslations("markets");
  const {
    shouldSearch,
    isLoading,
    recentEntries,
    showTrending,
    trendingMarkets,
    rowShared,
    onClearRecent,
    onRecentSelect
  } = props;
  let t0;
  if ($[0] !== onClearRecent || $[1] !== onRecentSelect || $[2] !== recentEntries || $[3] !== shouldSearch || $[4] !== t) {
    t0 = !shouldSearch && recentEntries.length > 0 && <CommandGroup heading={<div className="flex items-center justify-between pr-2"><span>{t("recent_heading")}</span><AnimatedButton size="xs" variant="tertiary" onClick={() => onClearRecent()} aria-label={t("recent_clear_aria")}>{t("recent_clear")}</AnimatedButton></div>}>{recentEntries.map(entry => <CommandItem key={`${entry.query}-${entry.ts}`} value={entry.query} onSelect={() => onRecentSelect(entry.query)} className="gap-3 rounded-control px-3 py-2.5"><Icon icon={Clock01Icon} className="size-4 text-foreground-secondary" /><span className="truncate text-body">{entry.query}</span></CommandItem>)}</CommandGroup>;
    $[0] = onClearRecent;
    $[1] = onRecentSelect;
    $[2] = recentEntries;
    $[3] = shouldSearch;
    $[4] = t;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== rowShared || $[7] !== shouldSearch || $[8] !== showTrending || $[9] !== tMarkets || $[10] !== trendingMarkets) {
    t1 = !shouldSearch && showTrending && <CommandGroup heading={tMarkets("sort.newest")}>{trendingMarkets.map(m => trendingRow(m, rowShared))}</CommandGroup>;
    $[6] = rowShared;
    $[7] = shouldSearch;
    $[8] = showTrending;
    $[9] = tMarkets;
    $[10] = trendingMarkets;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== isLoading || $[13] !== recentEntries || $[14] !== shouldSearch || $[15] !== showTrending || $[16] !== t) {
    t2 = !shouldSearch && recentEntries.length === 0 && !showTrending && !isLoading ? <div className="py-16 text-center text-body text-foreground-secondary">{t("type_to_search")}</div> : null;
    $[12] = isLoading;
    $[13] = recentEntries;
    $[14] = shouldSearch;
    $[15] = showTrending;
    $[16] = t;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== t0 || $[19] !== t1 || $[20] !== t2) {
    t3 = <>{t0}{t1}{t2}</>;
    $[18] = t0;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
}
function SearchNoResultsState(props) {
  const $ = _c(12);
  const t = useTranslations("search");
  const {
    shouldSearch,
    activeTabHasResults,
    isPending,
    isLoading,
    hasResults,
    showTrending,
    trendingMarkets,
    rowShared
  } = props;
  let t0;
  if ($[0] !== activeTabHasResults || $[1] !== hasResults || $[2] !== isLoading || $[3] !== isPending || $[4] !== rowShared || $[5] !== shouldSearch || $[6] !== showTrending || $[7] !== t || $[8] !== trendingMarkets) {
    t0 = shouldSearch && !activeTabHasResults && !isPending && !isLoading ? <><div className="flex flex-col items-center justify-center gap-2 py-16 text-center"><Icon icon={SearchRemoveIcon} className="size-8 text-foreground-tertiary" /><p className="text-body font-medium text-foreground-secondary">{t("no_results")}</p><p className="text-caption text-foreground-secondary/60">{t("no_results_hint")}</p></div>{!hasResults && showTrending && <CommandGroup heading={t("try_these_instead")}>{trendingMarkets.map(m => trendingRow(m, rowShared))}</CommandGroup>}</> : null;
    $[0] = activeTabHasResults;
    $[1] = hasResults;
    $[2] = isLoading;
    $[3] = isPending;
    $[4] = rowShared;
    $[5] = shouldSearch;
    $[6] = showTrending;
    $[7] = t;
    $[8] = trendingMarkets;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== t0) {
    t1 = <>{t0}</>;
    $[10] = t0;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
function SearchExactMatchGroup(props) {
  const $ = _c(16);
  const t = useTranslations("search");
  const {
    activeTab,
    exactMatchPubkey,
    hasExactMatch,
    markets,
    outcomes,
    winners,
    proposals,
    users,
    rowShared,
    onCommit,
    onHoverMarket,
    onHoverWinner
  } = props;
  let t0;
  if ($[0] !== activeTab || $[1] !== exactMatchPubkey || $[2] !== hasExactMatch || $[3] !== markets || $[4] !== onCommit || $[5] !== onHoverMarket || $[6] !== onHoverWinner || $[7] !== outcomes || $[8] !== proposals || $[9] !== rowShared || $[10] !== t || $[11] !== users || $[12] !== winners) {
    t0 = activeTab === "all" && exactMatchPubkey !== null && hasExactMatch ? <CommandGroup heading={t("exact_match_heading")}>{markets.flatMap(m => m.pubkey === exactMatchPubkey ? [buildMarketRow(m, rowShared, true)] : [])}{outcomes.flatMap(outcome => outcome.pubkey === exactMatchPubkey ? [<OutcomeRow key={outcome.pubkey} outcome={outcome} href={`/markets/${outcome.market}?outcome=${encodeURIComponent(outcome.label)}`} onCommit={onCommit} onHoverPrefetch={onHoverMarket} />] : [])}{winners.flatMap(winner => winner.pubkey === exactMatchPubkey || winner.mint === exactMatchPubkey ? [<WinnerRow key={winner.pubkey} winner={winner} href={winnerHref(winner)} onCommit={onCommit} onHoverPrefetch={onHoverWinner} />] : [])}{proposals.flatMap(p => p.proposalId === exactMatchPubkey ? [buildProposalRow(p, rowShared)] : [])}{users.filter(u => u.user.walletAddress === exactMatchPubkey).map((entry, idx) => buildUserRow(entry, idx, rowShared))}</CommandGroup> : null;
    $[0] = activeTab;
    $[1] = exactMatchPubkey;
    $[2] = hasExactMatch;
    $[3] = markets;
    $[4] = onCommit;
    $[5] = onHoverMarket;
    $[6] = onHoverWinner;
    $[7] = outcomes;
    $[8] = proposals;
    $[9] = rowShared;
    $[10] = t;
    $[11] = users;
    $[12] = winners;
    $[13] = t0;
  } else {
    t0 = $[13];
  }
  let t1;
  if ($[14] !== t0) {
    t1 = <>{t0}</>;
    $[14] = t0;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  return t1;
}
function SearchAllGroups(props) {
  const $ = _c(43);
  const t = useTranslations("search");
  const tLiveEvents = useTranslations("liveEvents.toolbar");
  const {
    showFuzzyGroups,
    blended,
    mode,
    marketByPubkey,
    proposalById,
    tickerHighlighted,
    onCommit,
    onHoverMarket,
    rowShared,
    liveEvents,
    outcomes,
    winners,
    creators,
    users,
    onHoverWinner
  } = props;
  if (!showFuzzyGroups) {
    return null;
  }
  let t0;
  if ($[0] !== blended || $[1] !== marketByPubkey || $[2] !== mode || $[3] !== onCommit || $[4] !== onHoverMarket || $[5] !== proposalById || $[6] !== rowShared || $[7] !== t || $[8] !== tickerHighlighted) {
    t0 = blended.length > 0 && <CommandGroup heading={mode === "ticker" ? t("ticker_matches") : undefined}><BlendedRowsList blended={blended} marketByPubkey={marketByPubkey} proposalById={proposalById} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} onHoverProposal={rowShared.onHoverProposal} /></CommandGroup>;
    $[0] = blended;
    $[1] = marketByPubkey;
    $[2] = mode;
    $[3] = onCommit;
    $[4] = onHoverMarket;
    $[5] = proposalById;
    $[6] = rowShared;
    $[7] = t;
    $[8] = tickerHighlighted;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== liveEvents || $[11] !== onCommit || $[12] !== tLiveEvents) {
    t1 = liveEvents.length > 0 ? <CommandGroup heading={tLiveEvents("title")}>{liveEvents.map(event => <LiveEventRowSearchItem key={event.id} event={event} onCommit={onCommit} />)}</CommandGroup> : null;
    $[10] = liveEvents;
    $[11] = onCommit;
    $[12] = tLiveEvents;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== onCommit || $[15] !== onHoverMarket || $[16] !== outcomes || $[17] !== t || $[18] !== tickerHighlighted) {
    t2 = outcomes.length > 0 && <CommandGroup heading={t("group_outcomes")}><OutcomeRowsList outcomes={outcomes} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} /></CommandGroup>;
    $[14] = onCommit;
    $[15] = onHoverMarket;
    $[16] = outcomes;
    $[17] = t;
    $[18] = tickerHighlighted;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== onCommit || $[21] !== onHoverWinner || $[22] !== t || $[23] !== tickerHighlighted || $[24] !== winners) {
    t3 = winners.length > 0 && <CommandGroup heading={t("group_winners")}><WinnerRowsList winners={winners} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverWinner={onHoverWinner} /></CommandGroup>;
    $[20] = onCommit;
    $[21] = onHoverWinner;
    $[22] = t;
    $[23] = tickerHighlighted;
    $[24] = winners;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  let t4;
  if ($[26] !== creators || $[27] !== onCommit || $[28] !== rowShared || $[29] !== t) {
    t4 = creators.length > 0 && <CommandGroup heading={t("group_creators")}><CreatorRowsList creators={creators} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} /></CommandGroup>;
    $[26] = creators;
    $[27] = onCommit;
    $[28] = rowShared;
    $[29] = t;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] !== onCommit || $[32] !== rowShared || $[33] !== t || $[34] !== users) {
    t5 = users.length > 0 && <CommandGroup heading={t("group_users")}><UserRowsList users={users} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} /></CommandGroup>;
    $[31] = onCommit;
    $[32] = rowShared;
    $[33] = t;
    $[34] = users;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== t0 || $[37] !== t1 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5) {
    t6 = <>{t0}{t1}{t2}{t3}{t4}{t5}</>;
    $[36] = t0;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  return t6;
}
type SearchTabGroupsProps = Pick<SearchResultsListProps, 'activeTab' | 'markets' | 'tickerHighlighted' | 'onCommit' | 'onHoverMarket' | 'liveEvents' | 'outcomes' | 'winners' | 'onHoverWinner' | 'proposals' | 'rowShared' | 'users'>;
function SearchTabGroups(props) {
  const $ = _c(2);
  if (!hasTabResults(props.activeTab, props)) {
    return null;
  }
  let t0;
  if ($[0] !== props) {
    t0 = <CommandGroup><SearchTabRows {...props} /></CommandGroup>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function SearchTabRows(t0) {
  const $ = _c(29);
  const {
    activeTab,
    markets,
    tickerHighlighted,
    onCommit,
    onHoverMarket,
    liveEvents,
    outcomes,
    winners,
    onHoverWinner,
    proposals,
    rowShared,
    users
  } = t0;
  switch (activeTab) {
    case "markets":
      {
        let t1;
        if ($[0] !== markets || $[1] !== onCommit || $[2] !== onHoverMarket || $[3] !== tickerHighlighted) {
          t1 = <MarketRowsList markets={markets} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} />;
          $[0] = markets;
          $[1] = onCommit;
          $[2] = onHoverMarket;
          $[3] = tickerHighlighted;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "live-events":
      {
        let t1;
        if ($[5] !== liveEvents || $[6] !== onCommit) {
          let t2;
          if ($[8] !== onCommit) {
            t2 = event => <LiveEventRowSearchItem key={event.id} event={event} onCommit={onCommit} />;
            $[8] = onCommit;
            $[9] = t2;
          } else {
            t2 = $[9];
          }
          t1 = liveEvents.map(t2);
          $[5] = liveEvents;
          $[6] = onCommit;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "outcomes":
      {
        let t1;
        if ($[10] !== onCommit || $[11] !== onHoverMarket || $[12] !== outcomes || $[13] !== tickerHighlighted) {
          t1 = <OutcomeRowsList outcomes={outcomes} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} />;
          $[10] = onCommit;
          $[11] = onHoverMarket;
          $[12] = outcomes;
          $[13] = tickerHighlighted;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    case "winners":
      {
        let t1;
        if ($[15] !== onCommit || $[16] !== onHoverWinner || $[17] !== tickerHighlighted || $[18] !== winners) {
          t1 = <WinnerRowsList winners={winners} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverWinner={onHoverWinner} />;
          $[15] = onCommit;
          $[16] = onHoverWinner;
          $[17] = tickerHighlighted;
          $[18] = winners;
          $[19] = t1;
        } else {
          t1 = $[19];
        }
        return t1;
      }
    case "proposals":
      {
        let t1;
        if ($[20] !== onCommit || $[21] !== proposals || $[22] !== rowShared.onHoverProposal || $[23] !== tickerHighlighted) {
          t1 = <ProposalRowsList proposals={proposals} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverProposal={rowShared.onHoverProposal} />;
          $[20] = onCommit;
          $[21] = proposals;
          $[22] = rowShared.onHoverProposal;
          $[23] = tickerHighlighted;
          $[24] = t1;
        } else {
          t1 = $[24];
        }
        return t1;
      }
    case "users":
      {
        let t1;
        if ($[25] !== onCommit || $[26] !== rowShared.onHoverUser || $[27] !== users) {
          t1 = <UserRowsList users={users} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} />;
          $[25] = onCommit;
          $[26] = rowShared.onHoverUser;
          $[27] = users;
          $[28] = t1;
        } else {
          t1 = $[28];
        }
        return t1;
      }
    case "all":
      {
        return null;
      }
  }
}